/*
 * Quick-Add / Product Modal — Collection pages
 * Adds a hover "Quick Add" button to each product card that opens a modal
 * with variant selection, subscribe toggle, delivery frequency, quantity,
 * and Add to Cart. Ported from the Coframe LN-110 variant to plain JS so it
 * runs natively in the theme (no JSX / TS build step).
 */
(function () {
  'use strict';

  console.log('[QA] Quick-Add / Product Modal loaded');

  // ─── Inline icons (replaces lucide) ─────────────────────────────────────────
  var X_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
  var PLUS_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>';

  // ─── State ──────────────────────────────────────────────────────────────────
  var currentProduct = null;
  var selectedVariantId = null;
  var selectedQty = 1;
  var purchaseType = 'one-time';
  var selectedSellingPlanId = null;
  var planNames = {};
  var typeOptionInfo = null;
  var currentCardDescription = '';

  // ─── Styling ──────────────────────────────────────────────────────────────
  var CF_STYLE_CONTENT = [
    '@keyframes cf-fadein { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }',
    '.cf-quick-add-btn { position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); white-space: nowrap; background: #56a512; color: #fff; border: none; border-radius: 9999px; padding: 10px 22px; font-size: 14px; font-weight: 700; cursor: pointer; opacity: 0; transition: opacity 0.2s ease, background 0.2s ease; display: flex; align-items: center; gap: 6px; letter-spacing: 0.04em; z-index: 10; }',
    '.cf-quick-add-btn svg { width: 14px; height: 14px; stroke-width: 2.5; }',
    '.cf-card-img-wrap:hover .cf-quick-add-btn, .cf-card-img-wrap:focus-within .cf-quick-add-btn { opacity: 1; }',
    '.cf-quick-add-btn:hover { background: #4a9010; }',
    '.cf-quick-add-btn:active { background: #3e780d; }',
    '@media (max-width: 767px), (hover: none) { .cf-quick-add-btn { opacity: 1; } }',
    '#cf-modal-overlay { display: none; position: fixed; inset: 0; z-index: 99999; background: rgba(0,0,0,0.52); align-items: center; justify-content: center; padding: 16px; }',
    '#cf-modal-overlay.cf-open { display: flex; }',
    '#cf-modal-container { background: #fff; border-radius: 12px; width: 100%; max-width: 600px; overflow: hidden; position: relative; animation: cf-fadein 0.22s ease; max-height: 90vh; overflow-y: auto; }',
    '#cf-modal-close-btn { position: absolute; top: 14px; right: 14px; z-index: 10; background: rgba(255,255,255,0.9); border: 1px solid #e5e7eb; border-radius: 9999px; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #1E4036; transition: background 0.15s; }',
    '#cf-modal-close-btn svg { width: 16px; height: 16px; stroke-width: 2; }',
    '.cf-modal-loading { display: flex; align-items: center; justify-content: center; height: 260px; }',
    '.cf-spinner { width: 36px; height: 36px; border: 3px solid #e5e7eb; border-top-color: #1E4036; border-radius: 50%; animation: cf-spin 0.7s linear infinite; }',
    '@keyframes cf-spin { to { transform: rotate(360deg); } }',
    '.cf-modal-body { display: flex; flex-direction: row; align-items: stretch; }',
    '.cf-modal-img-col { flex-shrink: 0; width: 230px; padding: 18px 16px; background: #F2EAE5; display: flex; align-items: center; justify-content: center; overflow: hidden; align-self: stretch; }',
    // `contain` (not `cover`): the column is tall and narrow, so cover crops
    // landscape lifestyle shots down to an unreadable strip.
    '.cf-modal-img-col img { width: 100%; height: auto; max-height: 100%; object-fit: contain; }',
    '.cf-modal-details-col { flex: 1; padding: 24px 24px 20px; display: flex; flex-direction: column; gap: 14px; }',
    '.cf-modal-title { font-size: 22px; font-weight: 700; color: #1E4036; line-height: 1.3; padding-right: 44px; }',
    '.cf-modal-price-row { display: flex; align-items: center; gap: 8px; font-size: 15px; color: #1E4036; }',
    '.cf-modal-price-original { text-decoration: line-through; color: #9ca3af; font-size: 13px; }',
    '.cf-modal-price-current { font-weight: 700; font-size: 17px; }',
    '.cf-modal-desc { font-size: 14px; color: #1E4036; opacity: 0.8; line-height: 1.5; }',
    '.cf-modal-options-label { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; }',
    '.cf-options-row { display: flex; flex-wrap: wrap; gap: 8px; }',
    '.cf-option-pill { padding: 6px 14px; border-radius: 9999px; border: 1.5px solid #d1d5db; background: #fff; font-size: 13px; color: #374151; cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s; }',
    '.cf-option-pill:hover { border-color: #1E4036; }',
    '.cf-option-pill.cf-selected { background: #1E4036; border-color: #1E4036; color: #fff; }',
    '.cf-option-pill.cf-unavailable { cursor: not-allowed; text-decoration: line-through; background: #cfcfd0; border-color: #cfcfd0; color: #9ca3af; }',
    '.cf-frequency-select { width: 100%; padding: 9px 36px 9px 14px; border: 1.5px solid #d1d5db; border-radius: 8px; background: #fff; font-size: 13px; color: #1E4036; cursor: pointer; appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%231E4036\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'m6 9 6 6 6-6\'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; }',
    '.cf-frequency-select:focus { outline: none; border-color: #1E4036; }',
    '.cf-purchase-type-row { display: flex; border: 1.5px solid #d1d5db; border-radius: 8px; overflow: hidden; }',
    '.cf-purchase-tab { flex: 1; padding: 8px 4px; background: #fff; border: none; font-size: 12px; font-weight: 600; color: #6b7280; cursor: pointer; text-align: center; transition: background 0.15s, color 0.15s; display: flex; flex-direction: column; align-items: center; gap: 2px; }',
    '.cf-purchase-tab.cf-selected { background: #1E4036; color: #fff; }',
    '.cf-purchase-tab .cf-tab-price { font-size: 11px; opacity: 0.8; }',
    '.cf-qty-row { display: flex; align-items: center; gap: 10px; }',
    '.cf-qty-label { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em; }',
    '.cf-qty-controls { display: flex; align-items: center; border: 1.5px solid #d1d5db; border-radius: 8px; overflow: hidden; }',
    '.cf-qty-btn { width: 32px; height: 32px; background: #fff; border: none; font-size: 16px; color: #1E4036; cursor: pointer; display: flex; align-items: center; justify-content: center; }',
    '.cf-qty-btn:hover { background: #f3f4f6; }',
    '.cf-qty-val { min-width: 32px; text-align: center; font-weight: 700; font-size: 14px; color: #1E4036; }',
    '.cf-atc-btn { width: 100%; background: #56a512; color: #fff; border: none; border-radius: 9999px; padding: 13px 24px; font-size: 14px; font-weight: 700; letter-spacing: 0.06em; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: background 0.2s; }',
    '.cf-atc-btn:hover { background: #4a9010; }',
    '.cf-atc-btn:disabled { opacity: 0.65; cursor: not-allowed; }',
    '.cf-atc-btn.cf-loading { pointer-events: none; }',
    '.cf-error-msg { color: #dc2626; font-size: 13px; }',
    '.cf-modal-footer { margin-top: auto; padding-top: 14px; display: flex; flex-direction: column; gap: 8px; }',
    '@media (max-width: 767px) {',
    '  .cf-modal-body { flex-direction: column; }',
    '  .cf-modal-img-col { width: 100% !important; max-width: 100% !important; height: 220px !important; padding: 0 !important; align-self: stretch !important; }',
    '  .cf-modal-img-col img { width: 100% !important; height: 100% !important; max-height: none !important; object-fit: cover !important; }',
    '  .cf-modal-footer { position: sticky; bottom: 0; z-index: 5; margin: 0 -24px -20px; padding: 28px 24px 16px; background: linear-gradient(to top, #fff 55%, rgba(255,255,255,0) 100%); pointer-events: none; }',
    '  .cf-modal-footer .cf-atc-btn, .cf-modal-footer #cf-modal-msg { pointer-events: auto; }',
    '  .cf-modal-footer .cf-atc-btn { box-shadow: 0 4px 14px rgba(0,0,0,0.18); }',
    '}'
  ].join('\n');

  var CF_TOOLTIP_CSS = [
    '.cf-option-pill[data-cf-tooltip] { position: relative; }',
    '.cf-option-pill[data-cf-tooltip]::after { content: attr(data-cf-tooltip); position: absolute; bottom: calc(100% + 9px); left: 50%; transform: translateX(-50%) translateY(4px); background: #1E4036; color: #fff; font-size: 12px; font-weight: 400; line-height: 1.45; padding: 8px 12px; border-radius: 8px; width: max-content; max-width: 210px; text-align: center; text-decoration: none; white-space: normal; opacity: 0; visibility: hidden; pointer-events: none; transition: opacity 0.15s ease, transform 0.15s ease; z-index: 60; box-shadow: 0 4px 12px rgba(0,0,0,0.18); }',
    ".cf-option-pill[data-cf-tooltip]::before { content: ''; position: absolute; bottom: calc(100% + 4px); left: 50%; transform: translateX(-50%) translateY(4px); border: 5px solid transparent; border-top-color: #1E4036; opacity: 0; visibility: hidden; pointer-events: none; transition: opacity 0.15s ease, transform 0.15s ease; z-index: 60; }",
    '.cf-option-pill[data-cf-tooltip]:hover::after, .cf-option-pill[data-cf-tooltip]:focus-visible::after, .cf-option-pill[data-cf-tooltip]:hover::before, .cf-option-pill[data-cf-tooltip]:focus-visible::before { opacity: 1; visibility: visible; transform: translateX(-50%) translateY(0); }'
  ].join('\n');

  function addStyling() {
    if (document.getElementById('cf-quick-add-styles')) return;
    var style = document.createElement('style');
    style.id = 'cf-quick-add-styles';
    style.textContent = CF_STYLE_CONTENT;
    document.head.appendChild(style);
  }

  function addTooltipStyling() {
    if (document.getElementById('cf-tooltip-styles')) return;
    var style = document.createElement('style');
    style.id = 'cf-tooltip-styles';
    style.textContent = CF_TOOLTIP_CSS;
    document.head.appendChild(style);
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatPrice(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function fetchProductJSON(handle) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('GET', '/products/' + handle + '.js', true);
      xhr.onload = function () {
        try { resolve(JSON.parse(xhr.responseText)); } catch (e) { reject(e); }
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send();
    });
  }

  function openCartDrawer() {
    try {
      if (window.SuperCart && typeof window.SuperCart.ShowCartfn === 'function') {
        window.SuperCart.ShowCartfn();
      } else {
        var btn = document.querySelector('.js-drawer-open-cart');
        if (btn) btn.click();
      }
    } catch (e) {
      console.warn('[QA] Could not open cart drawer', e);
    }
  }

  // ─── Modal DOM ───────────────────────────────────────────────────────────────
  function injectModal() {
    if (document.getElementById('cf-modal-overlay')) return;
    var overlay = document.createElement('div');
    overlay.id = 'cf-modal-overlay';
    overlay.innerHTML =
      '<div id="cf-modal-container">' +
        '<button id="cf-modal-close-btn" aria-label="Close modal">' + X_SVG + '</button>' +
        '<div id="cf-modal-inner">' +
          '<div class="cf-modal-loading" id="cf-modal-loading"><div class="cf-spinner"></div></div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    wireModalEvents(overlay);
  }

  function wireModalEvents(overlay) {
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });
    document.getElementById('cf-modal-close-btn').addEventListener('click', closeModal);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });
  }

  function openModal() {
    var overlay = document.getElementById('cf-modal-overlay');
    if (!overlay) return;
    overlay.classList.add('cf-open');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    var overlay = document.getElementById('cf-modal-overlay');
    if (!overlay) return;
    overlay.classList.remove('cf-open');
    document.body.style.overflow = '';
    currentProduct = null;
    selectedVariantId = null;
    selectedQty = 1;
    purchaseType = 'one-time';
    selectedSellingPlanId = null;
  }

  function showModalLoading() {
    var inner = document.getElementById('cf-modal-inner');
    if (inner) inner.innerHTML = '<div class="cf-modal-loading"><div class="cf-spinner"></div></div>';
  }

  function showModalError() {
    var inner = document.getElementById('cf-modal-inner');
    if (inner) inner.innerHTML = '<div class="cf-modal-loading"><span class="cf-error-msg">Failed to load product. Please try again.</span></div>';
  }

  // ─── Derive modal state from product data ──────────────────────────────────
  function computeSubscribePrice(basePrice, plan) {
    var adj = plan && plan.price_adjustments && plan.price_adjustments[0];
    if (!adj) return basePrice;
    if (adj.value_type === 'percentage') return Math.round(basePrice * (1 - adj.value / 100));
    if (adj.value_type === 'fixed_amount') return basePrice - adj.value;
    if (adj.value_type === 'price') return adj.value;
    return basePrice;
  }

  function computeImgSrc(product) {
    var fi = product.featured_image;
    if (fi) return fi.indexOf('//') === 0 ? 'https:' + fi : fi;
    return (product.images && product.images[0]) ? product.images[0].src || product.images[0] : '';
  }

  function getDefaultSellingPlan(product, hasSellingPlans) {
    if (!hasSellingPlans) return null;
    var g = product.selling_plan_groups[0];
    return (g && g.selling_plans && g.selling_plans[0]) || null;
  }

  function detectTypeOption(options) {
    for (var i = 0; i < (options || []).length; i++) {
      var opt = options[i];
      var name = typeof opt === 'string' ? opt : opt.name;
      var vals = (typeof opt === 'string' ? [] : opt.values) || [];
      var sub = vals.filter(function (v) { return /subscri/i.test(v); })[0];
      var one = vals.filter(function (v) { return /one[\s-]?time/i.test(v); })[0];
      if (sub && one) return { name: name, subscribeValue: sub, oneTimeValue: one };
    }
    return null;
  }

  function buildPlanNames(product) {
    var map = {};
    (product.selling_plan_groups || []).forEach(function (g) {
      (g.selling_plans || []).forEach(function (p) { map[p.id] = p.name; });
    });
    return map;
  }

  function computeModalState(product) {
    var variants = product.variants || [];
    var availableVariants = variants.filter(function (v) { return v.available; });
    var defaultVariant = availableVariants[0] || variants[0];
    var hasSellingPlans = !!(product.selling_plan_groups && product.selling_plan_groups.length > 0);
    var defaultSellingPlan = getDefaultSellingPlan(product, hasSellingPlans);
    var currentPrice = (defaultVariant && defaultVariant.price) || product.price_min;
    var subscribedPrice = computeSubscribePrice((defaultVariant && defaultVariant.price) || 0, defaultSellingPlan);
    return {
      variants: variants,
      defaultVariant: defaultVariant,
      hasSellingPlans: hasSellingPlans,
      requiresSellingPlan: !!product.requires_selling_plan,
      defaultSellingPlan: defaultSellingPlan,
      currentPrice: currentPrice,
      subscribedPrice: subscribedPrice,
      compareAtPrice: defaultVariant && defaultVariant.compare_at_price,
      options: product.options || [],
      showOptions: variants.length > 1,
      imgSrc: computeImgSrc(product),
      typeOption: detectTypeOption(product.options || [])
    };
  }

  function optionValuesFor(opt, idx, options, variants) {
    if (typeof opt !== 'string' && opt.values) return opt.values;
    var fieldKey = ['option1', 'option2', 'option3'][idx];
    var seen = {};
    var out = [];
    variants.forEach(function (v) {
      if (!(v[fieldKey] in seen)) { seen[v[fieldKey]] = 1; out.push(v[fieldKey]); }
    });
    return out;
  }

  // ─── Build modal body HTML ─────────────────────────────────────────────────
  function modalImageHtml(src, alt) {
    return '<div class="cf-modal-img-col"><img src="' + esc(src) + '" alt="' + esc(alt) + '" loading="lazy"></div>';
  }

  function modalPriceRowHtml(compareAtPrice, currentPrice) {
    var orig = (compareAtPrice && compareAtPrice > currentPrice)
      ? '<span class="cf-modal-price-original">' + formatPrice(compareAtPrice) + '</span>' : '';
    return '<div class="cf-modal-price-row">' + orig +
      '<span class="cf-modal-price-current" id="cf-modal-current-price">' + formatPrice(currentPrice) + '</span></div>';
  }

  function optionPillsHtml(option, values, availableVariants) {
    var pills = values.map(function (val) {
      var available = availableVariants.some(function (v) {
        return (v.option1 === val || v.option2 === val || v.option3 === val) && v.available;
      });
      return '<button class="cf-option-pill' + (available ? '' : ' cf-unavailable') + '" data-option="' +
        esc(option) + '" data-value="' + esc(val) + '"' + (available ? '' : ' disabled') + '>' + esc(val) + '</button>';
    }).join('');
    return '<div class="cf-option-group" data-option="' + esc(option) + '">' +
      '<div class="cf-modal-options-label">' + esc(option) + '</div>' +
      '<div class="cf-options-row">' + pills + '</div></div>';
  }

  function purchaseTypeSelectorHtml(o) {
    if (!o.hasSellingPlans || o.hasTypeOption) return '';
    if (o.requiresSellingPlan) {
      return '<div class="cf-purchase-type-row"><button class="cf-purchase-tab cf-selected" data-purchase-type="subscribe">' +
        '<span>Subscribe &amp; Save</span><span class="cf-tab-price">' + formatPrice(o.subscribedPrice) + '</span></button></div>';
    }
    return '<div class="cf-purchase-type-row">' +
      '<button class="cf-purchase-tab cf-selected" data-purchase-type="one-time"><span>One-Time</span>' +
      '<span class="cf-tab-price">' + formatPrice(o.oneTimePrice) + '</span></button>' +
      '<button class="cf-purchase-tab" data-purchase-type="subscribe"><span>Subscribe &amp; Save</span>' +
      '<span class="cf-tab-price">' + formatPrice(o.subscribedPrice) + '</span></button></div>';
  }

  function qtySelectorHtml() {
    return '<div class="cf-qty-row"><span class="cf-qty-label">Qty</span>' +
      '<div class="cf-qty-controls"><button class="cf-qty-btn" id="cf-qty-minus">−</button>' +
      '<span class="cf-qty-val" id="cf-qty-display">1</span>' +
      '<button class="cf-qty-btn" id="cf-qty-plus">+</button></div></div>';
  }

  function modalFooterHtml() {
    return '<div class="cf-modal-footer"><button class="cf-atc-btn" id="cf-atc-btn">ADD TO CART</button>' +
      '<div id="cf-modal-msg"></div></div>';
  }

  function buildModalOptionsHtml(s) {
    if (!s.showOptions) return '';
    return s.options.map(function (opt, idx) {
      var optName = typeof opt === 'string' ? opt : opt.name;
      var vals = optionValuesFor(opt, idx, s.options, s.variants);
      return optionPillsHtml(optName, vals, s.variants);
    }).join('');
  }

  function buildModalBodyHtml(product, s) {
    var desc = currentCardDescription ? '<div class="cf-modal-desc">' + esc(currentCardDescription) + '</div>' : '';
    return '<div class="cf-modal-body">' +
      modalImageHtml(s.imgSrc, product.title) +
      '<div class="cf-modal-details-col">' +
        '<div class="cf-modal-title">' + esc(product.title) + '</div>' +
        modalPriceRowHtml(s.compareAtPrice, s.currentPrice) +
        desc +
        buildModalOptionsHtml(s) +
        purchaseTypeSelectorHtml({
          hasSellingPlans: s.hasSellingPlans,
          requiresSellingPlan: s.requiresSellingPlan,
          hasTypeOption: !!s.typeOption,
          oneTimePrice: s.currentPrice,
          subscribedPrice: s.subscribedPrice
        }) +
        '<div id="cf-frequency-wrap"></div>' +
        qtySelectorHtml() +
        modalFooterHtml() +
      '</div></div>';
  }

  // ─── Selected-variant helpers & delivery frequency ─────────────────────────
  function getSelectedVariant() {
    if (!currentProduct || !selectedVariantId) return null;
    return (currentProduct.variants || []).filter(function (v) { return v.id === selectedVariantId; })[0] || null;
  }

  function optionIndexByName(name) {
    if (!currentProduct) return -1;
    return (currentProduct.options || []).findIndex(function (o) {
      return (typeof o === 'string' ? o : o.name) === name;
    });
  }

  function isSubscribeMode() {
    if (typeOptionInfo) {
      var v = getSelectedVariant();
      if (!v) return false;
      var idx = optionIndexByName(typeOptionInfo.name);
      if (idx === -1) return false;
      return v['option' + (idx + 1)] === typeOptionInfo.subscribeValue;
    }
    return purchaseType === 'subscribe';
  }

  function computeSelectedPrice(v) {
    if (isSubscribeMode() && selectedSellingPlanId) {
      var alloc = (v.selling_plan_allocations || []).filter(function (a) {
        return a.selling_plan_id === selectedSellingPlanId;
      })[0];
      if (alloc) return alloc.price;
    }
    return v.price;
  }

  function updateCompareAtDisplay(v, price) {
    var origEl = document.querySelector('.cf-modal-price-original');
    if (!origEl) return;
    var cmp = v.compare_at_price;
    origEl.style.display = cmp && cmp > price ? '' : 'none';
    if (cmp) origEl.textContent = formatPrice(cmp);
  }

  function updatePriceDisplay() {
    var v = getSelectedVariant();
    if (!v) return;
    var price = computeSelectedPrice(v);
    var priceEl = document.getElementById('cf-modal-current-price');
    if (priceEl) priceEl.textContent = formatPrice(price);
    updateCompareAtDisplay(v, price);
  }

  function wireFrequencySelect(wrap) {
    var select = wrap.querySelector('.cf-frequency-select');
    if (!select) return;
    select.addEventListener('change', function () {
      selectedSellingPlanId = Number(select.value);
      updatePriceDisplay();
    });
  }

  var PREFER_3MO_HANDLES = ['prolon-offer', 'prolon-next-gen-v4'];

  function defaultPlanIdFor(planIds) {
    if (currentProduct && PREFER_3MO_HANDLES.indexOf(currentProduct.handle) !== -1) {
      var threeMo = planIds.filter(function (pid) { return /3\s*month/i.test(planNames[pid] || ''); })[0];
      if (threeMo) return threeMo;
    }
    return planIds[0];
  }

  function renderFrequencySelector() {
    var wrap = document.getElementById('cf-frequency-wrap');
    if (!wrap) return;
    var v = getSelectedVariant();
    var planIds = v ? (v.selling_plan_allocations || []).map(function (a) { return a.selling_plan_id; }) : [];
    if (!isSubscribeMode() || planIds.length === 0) {
      wrap.innerHTML = '';
      selectedSellingPlanId = null;
      return;
    }
    if (selectedSellingPlanId === null || planIds.indexOf(selectedSellingPlanId) === -1) {
      selectedSellingPlanId = defaultPlanIdFor(planIds);
    }
    var opts = planIds.map(function (pid) {
      return '<option value="' + pid + '"' + (pid === selectedSellingPlanId ? ' selected' : '') + '>' +
        esc(planNames[pid] || 'Plan') + '</option>';
    }).join('');
    wrap.innerHTML = '<div class="cf-option-group"><div class="cf-modal-options-label">Delivery Frequency</div>' +
      '<select class="cf-frequency-select" aria-label="Delivery Frequency">' + opts + '</select></div>';
    wireFrequencySelect(wrap);
  }

  // ─── Wire modal interactions ─────────────────────────────────────────────────
  function markDefaultPills(inner, options, defaultVariant) {
    options.forEach(function (opt, idx) {
      var optName = typeof opt === 'string' ? opt : opt.name;
      var val = defaultVariant['option' + (idx + 1)];
      var pill = inner.querySelector('.cf-option-pill[data-option="' + optName + '"][data-value="' + val + '"]');
      if (pill) pill.classList.add('cf-selected');
    });
  }

  function findMatchingVariant(inner, variants, options) {
    var selections = {};
    inner.querySelectorAll('.cf-option-pill.cf-selected').forEach(function (p) {
      selections[p.getAttribute('data-option')] = p.getAttribute('data-value');
    });
    return variants.filter(function (v) {
      return options.every(function (opt, idx) {
        var oName = typeof opt === 'string' ? opt : opt.name;
        return !selections[oName] || v['option' + (idx + 1)] === selections[oName];
      });
    })[0];
  }

  function wireOptionPills(inner, variants, options) {
    inner.querySelectorAll('.cf-option-pill').forEach(function (pill) {
      if (pill.classList.contains('cf-unavailable')) return;
      if (pill.hasAttribute('data-plan-id')) return;
      pill.addEventListener('click', function () {
        var optName = pill.getAttribute('data-option');
        inner.querySelectorAll('.cf-option-pill[data-option="' + optName + '"]').forEach(function (p) {
          p.classList.remove('cf-selected');
        });
        pill.classList.add('cf-selected');
        var matched = findMatchingVariant(inner, variants, options);
        if (matched) {
          selectedVariantId = matched.id;
          renderFrequencySelector();
          updatePriceDisplay();
        }
      });
    });
  }

  function wirePurchaseTabs(inner) {
    inner.querySelectorAll('.cf-purchase-tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        inner.querySelectorAll('.cf-purchase-tab').forEach(function (t) { t.classList.remove('cf-selected'); });
        tab.classList.add('cf-selected');
        purchaseType = tab.dataset.purchaseType;
        renderFrequencySelector();
        updatePriceDisplay();
      });
    });
  }

  function updateQtyDisplay() {
    var el = document.getElementById('cf-qty-display');
    if (el) el.textContent = String(selectedQty);
  }

  function wireQtyButtons() {
    var minus = document.getElementById('cf-qty-minus');
    var plus = document.getElementById('cf-qty-plus');
    if (minus) minus.addEventListener('click', function () {
      if (selectedQty > 1) { selectedQty--; updateQtyDisplay(); }
    });
    if (plus) plus.addEventListener('click', function () {
      selectedQty++; updateQtyDisplay();
    });
  }

  // ─── Flavor tooltips (5-Day FMD kit options) ───────────────────────────────
  var FLAVOR_TOOLTIPS = {
    expandedvariety: 'Mix of soups, bars & shake — classic and bold powdered soups, plus a Fasting Shake and Fasting Bars for variety',
    nextgen: 'Ready-to-eat organic soups (premium)',
    boldflavors: 'New bold soup flavors like Curry & Ginger',
    gen3: 'New bold soup flavors like Curry & Ginger',
    original: 'Classic powdered soups — mix, heat, enjoy'
  };

  function applyFlavorTooltips(inner) {
    addTooltipStyling();
    inner.querySelectorAll('.cf-option-pill').forEach(function (pill) {
      var norm = (pill.getAttribute('data-value') || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      var keys = Object.keys(FLAVOR_TOOLTIPS);
      for (var i = 0; i < keys.length; i++) {
        if (norm.indexOf(keys[i]) !== -1) {
          pill.setAttribute('data-cf-tooltip', FLAVOR_TOOLTIPS[keys[i]]);
          break;
        }
      }
    });
  }

  function wireModalInteractions(inner, s) {
    if (s.showOptions && s.defaultVariant) markDefaultPills(inner, s.options, s.defaultVariant);
    wireOptionPills(inner, s.variants, s.options);
    wirePurchaseTabs(inner);
    wireQtyButtons();
    applyFlavorTooltips(inner);
    var atc = document.getElementById('cf-atc-btn');
    if (atc) atc.addEventListener('click', handleAddToCart);
  }

  // ─── Optional modal body overrides ─────────────────────────────────────────
  // Other scripts (e.g. dk-fmd-buy-box.js) can register a renderer on
  // window.DK_QUICK_ADD_OVERRIDES to take over the modal body for specific
  // products. A renderer gets (inner, product) and returns true once it has
  // rendered and wired the body itself; otherwise the default modal is used.
  function runModalOverrides(inner, product) {
    var overrides = window.DK_QUICK_ADD_OVERRIDES || [];
    for (var i = 0; i < overrides.length; i++) {
      try {
        if (overrides[i](inner, product) === true) return true;
      } catch (e) {
        console.warn('[QA] Modal override failed', e);
      }
    }
    return false;
  }

  // ─── Populate modal with product data ──────────────────────────────────────
  function populateModal(product) {
    currentProduct = product;
    selectedQty = 1;

    var inner = document.getElementById('cf-modal-inner');
    if (!inner) return;
    if (runModalOverrides(inner, product)) return;

    var s = computeModalState(product);
    planNames = buildPlanNames(product);
    typeOptionInfo = s.typeOption;
    purchaseType = s.requiresSellingPlan ? 'subscribe' : 'one-time';
    selectedVariantId = (s.defaultVariant && s.defaultVariant.id) || null;
    selectedSellingPlanId = null;

    inner.innerHTML = '<div>' + buildModalBodyHtml(product, s) + '</div>';
    wireModalInteractions(inner, s);
    renderFrequencySelector();
    updatePriceDisplay();
  }

  // ─── Add to Cart handler ───────────────────────────────────────────────────
  function setAtcLoading(atcBtn, loading) {
    atcBtn.disabled = loading;
    atcBtn.classList.toggle('cf-loading', loading);
    atcBtn.textContent = loading ? 'Adding...' : 'ADD TO CART';
  }

  function showAtcError(msgEl) {
    if (msgEl) msgEl.innerHTML = '<span class="cf-error-msg">Item could not be added. Please try again.</span>';
  }

  function requestCartAdd(planArg, props) {
    var body = { id: selectedVariantId, quantity: selectedQty, properties: props };
    if (planArg) body.selling_plan = planArg;
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', '/cart/add.js', true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch (e) { resolve({}); }
        } else {
          reject(new Error('Cart add failed: ' + xhr.status));
        }
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(JSON.stringify(body));
    });
  }

  // Product handles that carry the Free Welcome Kit offer on subscription.
  // Adding the `_free_welcome_kit` property makes the cart drawer auto-add the
  // gift variant (47948612665502), same as the product page (PDP) behavior.
  var WELCOME_KIT_HANDLES = ['prolon-offer'];

  function buildCartProperties() {
    var props = {};
    if (currentProduct &&
        WELCOME_KIT_HANDLES.indexOf(currentProduct.handle) !== -1 &&
        isSubscribeMode()) {
      props._free_welcome_kit = true;
    }
    return props;
  }

  function handleAddToCart() {
    var atcBtn = document.getElementById('cf-atc-btn');
    var msgEl = document.getElementById('cf-modal-msg');
    if (!selectedVariantId || !atcBtn) return;

    setAtcLoading(atcBtn, true);
    if (msgEl) msgEl.innerHTML = '';

    var planArg = (isSubscribeMode() && selectedSellingPlanId) ? selectedSellingPlanId : undefined;

    requestCartAdd(planArg, buildCartProperties()).then(function () {
      closeModal();
      openCartDrawer();
    }).catch(function (err) {
      console.warn('[QA] Add to cart error', err);
      showAtcError(msgEl);
      setAtcLoading(atcBtn, false);
    });
  }

  // ─── Quick Add button click handler ─────────────────────────────────────────
  function handleQuickAddClick(e) {
    e.preventDefault();
    e.stopPropagation();
    var btn = e.currentTarget;
    var handle = btn.dataset.productHandle;
    if (!handle) return;

    currentCardDescription = btn.dataset.productDescription || '';
    openModal();
    showModalLoading();

    fetchProductJSON(handle).then(function (product) {
      populateModal(product);
    }).catch(function (err) {
      console.error('[QA] Failed to fetch product', err);
      showModalError();
    });
  }

  // ─── Inject Quick Add button into each card ─────────────────────────────────
  function injectQuickAddButton(card) {
    if (card.getAttribute('data-cf-quick-add-injected')) return;

    var productId = card.dataset.productId || '';
    var imgLink = card.querySelector('a[href*="/products/"]');
    if (!imgLink) return;

    // Prefer the real product handle from the card (used for fetching the
    // correct product data). The anchor href may be overridden for navigation
    // only (e.g. prolon-offer links to /products/prolon-offer).
    var handleEl = card.querySelector('[data-cf-handle]');
    var handle = handleEl ? handleEl.getAttribute('data-cf-handle') : '';
    if (!handle) {
      var href = imgLink.getAttribute('href') || '';
      handle = href.replace('/products/', '').split('?')[0];
    }
    if (!handle) return;

    var imgWrapper = imgLink.querySelector('div.relative');
    if (!imgWrapper) return;

    var descEl = card.querySelector('[data-cf-desc]') || card.querySelector('[class*="line-clamp"]');
    var description = descEl ? (descEl.getAttribute('data-cf-desc') || (descEl.textContent || '').trim()) : '';

    imgLink.classList.add('cf-card-img-wrap');

    var btn = document.createElement('button');
    btn.className = 'cf-quick-add-btn';
    btn.setAttribute('data-product-handle', handle);
    btn.setAttribute('data-product-id', productId);
    btn.setAttribute('data-product-description', description);
    btn.setAttribute('aria-label', 'Quick Add to cart');
    btn.type = 'button';
    btn.innerHTML = PLUS_SVG + 'Quick Add';
    imgWrapper.appendChild(btn);
    btn.addEventListener('click', handleQuickAddClick);

    card.setAttribute('data-cf-quick-add-injected', 'true');
  }

  function processCards() {
    var cards = document.querySelectorAll('li.product-grid__item:not([data-cf-quick-add-injected])');
    cards.forEach(injectQuickAddButton);
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    addStyling();
    injectModal();
    processCards();

    var observer = new MutationObserver(function () {
      var unprocessed = document.querySelectorAll('li.product-grid__item:not([data-cf-quick-add-injected])');
      if (unprocessed.length > 0) processCards();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Small surface for modal overrides that need to close up after adding.
  window.DKQuickAdd = {
    closeModal: closeModal,
    openCartDrawer: openCartDrawer
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
